import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { 
  Trophy, ArrowCounterClockwise, Play, Users, Fire, 
  Clock, ArrowsDownUp, Trash, CaretUp, CaretDown,
  ShareNetwork, CheckCircle, X, Warning, Basketball
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';

// Poll interval for real-time sync (milliseconds)
const POLL_INTERVAL = 2000;

export function OpenRunTracker() {
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Setup state
  const [showSetup, setShowSetup] = useState(false);
  const [teamCount, setTeamCount] = useState(4);
  const [teamA, setTeamA] = useState(1);
  const [teamB, setTeamB] = useState(2);
  const [eventId, setEventId] = useState('');
  const [creating, setCreating] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // UI state
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showEditQueue, setShowEditQueue] = useState(false);
  const [showGameLog, setShowGameLog] = useState(false);
  const [editQueue, setEditQueue] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  // Report state
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Polling ref
  const versionRef = useRef(0);
  const pollTimeoutRef = useRef(null);

  // Check auth and load session
  useEffect(() => {
    checkAuthAndLoad();
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const checkAuthAndLoad = async () => {
    try {
      // Check if user is admin/staff
      const authResponse = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (authResponse.ok) {
        const user = await authResponse.json();
        setIsAdmin(user.role === 'admin' || user.role === 'staff');
      }
      
      // Load available sessions for dropdown
      await loadAvailableSessions();
      
      // Try to load existing session from URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session');
      
      if (sessionId) {
        await loadSession(sessionId);
      } else {
        // Try to get recent sessions
        const sessionsResponse = await fetch(`${getApiBaseUrl()}/api/open-run/my-sessions`, {
          credentials: 'include'
        });
        if (sessionsResponse.ok) {
          const sessions = await sessionsResponse.json();
          const activeSession = sessions.find(s => s.status === 'active');
          if (activeSession) {
            await loadSession(activeSession.session_id);
            // Update URL without reload
            window.history.replaceState({}, '', `?session=${activeSession.session_id}`);
          } else {
            setShowSetup(true);
          }
        } else {
          setShowSetup(true);
        }
      }
    } catch (error) {
      console.error('Init error:', error);
      setShowSetup(true);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSessions = async () => {
    setLoadingSessions(true);
    try {
      // Fetch upcoming/today sessions
      const response = await fetch(`${getApiBaseUrl()}/api/sessions`);
      if (response.ok) {
        const allSessions = await response.json();
        // Filter to today and future sessions that are active
        const today = new Date().toISOString().split('T')[0];
        const upcomingSessions = allSessions.filter(s => 
          s.date >= today && s.status !== 'ended'
        ).sort((a, b) => {
          // Sort by date, then by start_time
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return a.start_time.localeCompare(b.start_time);
        });
        setAvailableSessions(upcomingSessions);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${sessionId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        versionRef.current = data.version;
        setShowSetup(false);
        // Start polling
        startPolling(sessionId);
      } else {
        toast.error('Session not found');
        setShowSetup(true);
      }
    } catch (error) {
      console.error('Load session error:', error);
      toast.error('Failed to load session');
    }
  };

  const startPolling = useCallback((sessionId) => {
    const poll = async () => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/open-run/sessions/${sessionId}/poll?version=${versionRef.current}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.updated && data.session) {
            setSession(data.session);
            versionRef.current = data.session.version;
          }
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
      pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
    };
    
    // Clear existing poll
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    poll();
  }, []);

  const createSession = async () => {
    if (!eventId.trim()) {
      toast.error('Please enter an event name');
      return;
    }
    if (teamA === teamB) {
      toast.error('Please select different teams for the first matchup');
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_id: eventId,
          team_count: teamCount,
          team_a: teamA,
          team_b: teamB
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        versionRef.current = data.version;
        setShowSetup(false);
        // Update URL
        window.history.replaceState({}, '', `?session=${data.session_id}`);
        toast.success('Open run started!');
        startPolling(data.session_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      console.error('Create session error:', error);
      toast.error('Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const recordGame = async (winner) => {
    if (processing) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${session.session_id}/record-game`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ winner })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        versionRef.current = data.version;
        
        // Check if 3-win rule triggered
        const lastGame = data.games[0];
        if (lastGame?.three_win_rule_triggered) {
          toast.success(`Team ${winner} hit 3 wins! Taking a seat.`, {
            icon: <Fire className="text-orange-500" />,
            duration: 4000
          });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record game');
      }
    } catch (error) {
      console.error('Record game error:', error);
      toast.error('Failed to record game');
    } finally {
      setProcessing(false);
    }
  };

  const undoLastGame = async () => {
    if (processing) return;
    setProcessing(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${session.session_id}/undo`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        versionRef.current = data.version;
        toast.success('Last game undone');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Nothing to undo');
      }
    } catch (error) {
      console.error('Undo error:', error);
      toast.error('Failed to undo');
    } finally {
      setProcessing(false);
    }
  };

  const saveQueueOrder = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${session.session_id}/reorder-queue`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_order: editQueue })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        versionRef.current = data.version;
        setShowEditQueue(false);
        toast.success('Queue reordered');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reorder');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder queue');
    }
  };

  const resetSession = async () => {
    try {
      await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${session.session_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      setSession(null);
      setShowSetup(true);
      setShowConfirmReset(false);
      window.history.replaceState({}, '', window.location.pathname);
      toast.success('Run reset');
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset');
    }
  };
  
  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/open-run/sessions/${session.session_id}/report`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReport(data);
        setShowReport(true);
      } else {
        toast.error('Failed to load report');
      }
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  const moveQueueTeam = (index, direction) => {
    const newQueue = [...editQueue];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newQueue.length) return;
    [newQueue[index], newQueue[newIndex]] = [newQueue[newIndex], newQueue[index]];
    setEditQueue(newQueue);
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?session=${session.session_id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  // Setup screen
  if (showSetup) {
    return (
      <div className="min-h-screen bg-black text-white p-4 sm:p-6">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <Basketball size={36} weight="duotone" className="text-orange-500 sm:w-12 sm:h-12" />
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight">OPEN RUN</h1>
            </div>
            <p className="text-gray-400 text-sm sm:text-base">Start a new live game tracker</p>
          </div>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Session Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Select Session</label>
                {loadingSessions ? (
                  <div className="flex items-center justify-center h-12 bg-zinc-800 rounded-lg">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : availableSessions.length === 0 ? (
                  <div className="text-center py-4 bg-zinc-800 rounded-lg">
                    <p className="text-gray-500 text-sm">No upcoming sessions found</p>
                    <Input
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      placeholder="Enter event name manually"
                      className="bg-zinc-700 border-zinc-600 text-white text-base mt-3 h-12"
                    />
                  </div>
                ) : (
                  <select
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    className="w-full h-12 sm:h-14 px-4 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-base sm:text-lg appearance-none cursor-pointer focus:outline-none focus:border-orange-500"
                    data-testid="session-picker"
                  >
                    <option value="">-- Select a session --</option>
                    {availableSessions.map((session) => {
                      // Format the session label
                      const date = new Date(session.date + 'T00:00:00');
                      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      const formatTime = (t) => {
                        const [h, m] = t.split(':');
                        const hour = parseInt(h);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour % 12 || 12;
                        return `${hour12}:${m} ${ampm}`;
                      };
                      const locationName = session.location?.name || 'TBD';
                      const label = `${dayName} ${monthDay} @ ${formatTime(session.start_time)} - ${locationName}`;
                      
                      return (
                        <option key={session.session_id} value={label}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
              
              {/* Team Count */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2 sm:mb-3">Number of Teams</label>
                <div className="grid grid-cols-4 gap-2">
                  {[3, 4, 5, 6].map((num) => (
                    <button
                      key={num}
                      onClick={() => setTeamCount(num)}
                      className={`h-12 sm:h-16 text-xl sm:text-2xl font-bold rounded-lg sm:rounded-xl transition-all ${
                        teamCount === num
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* First Matchup */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2 sm:mb-3">First Game</label>
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Team A</p>
                    <div className={`grid gap-1 ${teamCount > 4 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {Array.from({ length: teamCount }, (_, i) => i + 1).map((num) => (
                        <button
                          key={num}
                          onClick={() => setTeamA(num)}
                          disabled={num === teamB}
                          className={`h-10 sm:h-12 text-base sm:text-lg font-bold rounded-lg transition-all ${
                            teamA === num
                              ? 'bg-green-500 text-white'
                              : num === teamB
                                ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                                : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span className="text-xl sm:text-2xl font-black text-gray-600">VS</span>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Team B</p>
                    <div className={`grid gap-1 ${teamCount > 4 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {Array.from({ length: teamCount }, (_, i) => i + 1).map((num) => (
                        <button
                          key={num}
                          onClick={() => setTeamB(num)}
                          disabled={num === teamA}
                          className={`h-10 sm:h-12 text-base sm:text-lg font-bold rounded-lg transition-all ${
                            teamB === num
                              ? 'bg-blue-500 text-white'
                              : num === teamA
                                ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                                : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Start Button */}
              <Button
                onClick={createSession}
                disabled={creating || !eventId.trim()}
                className="w-full h-12 sm:h-16 text-lg sm:text-xl font-bold bg-orange-500 hover:bg-orange-600 text-white"
              >
                {creating ? <LoadingSpinner /> : (
                  <>
                    <Play size={20} className="mr-2 sm:w-6 sm:h-6" weight="fill" />
                    START RUN
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main live tracker
  if (!session) return null;

  const teams = session.teams;
  const currentA = session.current_team_a;
  const currentB = session.current_team_b;
  const queue = session.queue_order;
  const teamAData = teams[String(currentA)];
  const teamBData = teams[String(currentB)];

  return (
    <div className="min-h-screen bg-black text-white pb-32" data-testid="open-run-tracker">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Basketball size={28} weight="duotone" className="text-orange-500" />
            <div>
              <h1 className="text-lg font-bold">OPEN RUN</h1>
              <p className="text-xs text-gray-500">{session.event_id} • Game #{session.game_count + 1}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadReport}
              disabled={loadingReport}
              className="text-orange-400 hover:text-orange-300 px-2"
            >
              {loadingReport ? <LoadingSpinner size="sm" /> : <Trophy size={20} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={copyShareLink}
              className="text-gray-400 hover:text-white px-2"
            >
              <ShareNetwork size={20} />
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConfirmReset(true)}
                className="text-red-400 hover:text-red-300 px-2"
              >
                <Trash size={20} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ON COURT - Main Matchup */}
      <div className="p-3 sm:p-4">
        <p className="text-center text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 sm:mb-3">On Court</p>
        
        <div className="flex gap-2 sm:gap-4">
          {/* Team A Button */}
          <button
            onClick={() => isAdmin && recordGame(currentA)}
            disabled={!isAdmin || processing}
            className={`flex-1 bg-gradient-to-b from-zinc-800 to-zinc-900 border-2 rounded-xl sm:rounded-2xl p-3 sm:p-6 transition-all active:scale-95 ${
              isAdmin ? 'border-green-500/50 hover:border-green-500 hover:shadow-lg hover:shadow-green-500/20' : 'border-zinc-700'
            }`}
          >
            <div className="text-center">
              <p className="text-5xl sm:text-7xl font-black mb-1 sm:mb-2">{currentA}</p>
              <p className="text-xs sm:text-sm font-medium text-gray-400">TEAM {currentA}</p>
              {teamAData.current_streak > 0 && (
                <div className="mt-2 sm:mt-3 flex items-center justify-center gap-1">
                  <Fire size={16} className="text-orange-500 sm:w-5 sm:h-5" weight="fill" />
                  <span className="text-orange-500 font-bold text-xs sm:text-base">{teamAData.current_streak} WIN{teamAData.current_streak > 1 ? 'S' : ''}</span>
                </div>
              )}
            </div>
          </button>
          
          {/* VS */}
          <div className="flex items-center">
            <span className="text-xl sm:text-3xl font-black text-zinc-700">VS</span>
          </div>
          
          {/* Team B Button */}
          <button
            onClick={() => isAdmin && recordGame(currentB)}
            disabled={!isAdmin || processing}
            className={`flex-1 bg-gradient-to-b from-zinc-800 to-zinc-900 border-2 rounded-xl sm:rounded-2xl p-3 sm:p-6 transition-all active:scale-95 ${
              isAdmin ? 'border-blue-500/50 hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20' : 'border-zinc-700'
            }`}
          >
            <div className="text-center">
              <p className="text-5xl sm:text-7xl font-black mb-1 sm:mb-2">{currentB}</p>
              <p className="text-xs sm:text-sm font-medium text-gray-400">TEAM {currentB}</p>
              {teamBData.current_streak > 0 && (
                <div className="mt-2 sm:mt-3 flex items-center justify-center gap-1">
                  <Fire size={16} className="text-orange-500 sm:w-5 sm:h-5" weight="fill" />
                  <span className="text-orange-500 font-bold text-xs sm:text-base">{teamBData.current_streak} WIN{teamBData.current_streak > 1 ? 'S' : ''}</span>
                </div>
              )}
            </div>
          </button>
        </div>
        
        {isAdmin && (
          <p className="text-center text-xs text-gray-600 mt-2 sm:mt-3">Tap winning team to record result</p>
        )}
      </div>

      {/* WAITING ORDER */}
      <div className="px-3 sm:px-4 mb-4 sm:mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Next Up</p>
          {isAdmin && queue.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditQueue([...queue]);
                setShowEditQueue(true);
              }}
              className="text-xs text-gray-500 hover:text-white h-7 px-2"
            >
              <ArrowsDownUp size={14} className="mr-1" />
              Edit
            </Button>
          )}
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0">
          {queue.length === 0 ? (
            <p className="text-gray-600 text-sm">No teams waiting</p>
          ) : (
            queue.map((teamNum, index) => (
              <div
                key={teamNum}
                className="flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 min-w-[60px] sm:min-w-[80px] text-center"
              >
                <p className="text-[10px] sm:text-xs text-gray-600 mb-0.5 sm:mb-1">{index + 1}{index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'}</p>
                <p className="text-xl sm:text-2xl font-black">{teamNum}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* TEAM STATS */}
      <div className="px-3 sm:px-4 mb-4 sm:mb-6">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 sm:mb-3">Team Stats</p>
        <div className={`grid gap-2 ${session.team_count > 4 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}>
          {Object.values(teams).map((team) => (
            <div
              key={team.team_number}
              className={`bg-zinc-900 border rounded-lg sm:rounded-xl p-2 sm:p-3 ${
                team.team_number === currentA || team.team_number === currentB
                  ? 'border-orange-500/50'
                  : 'border-zinc-800'
              }`}
            >
              <div className="flex items-center justify-between mb-1 sm:mb-2">
                <span className="text-lg sm:text-xl font-black">#{team.team_number}</span>
                {team.current_streak > 0 && (
                  <div className="flex items-center gap-0.5 text-orange-500">
                    <Fire size={12} weight="fill" className="sm:w-[14px] sm:h-[14px]" />
                    <span className="text-[10px] sm:text-xs font-bold">{team.current_streak}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1 text-[10px] sm:text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">W</span>
                  <span className="font-bold text-green-500">{team.total_wins}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">L</span>
                  <span className="font-bold text-red-500">{team.total_losses}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Best</span>
                  <span className="font-bold">{team.longest_streak}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">3-Win</span>
                  <span className="font-bold text-orange-500">{team.three_win_runs}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* GAME LOG */}
      <div className="px-3 sm:px-4 mb-4 sm:mb-6">
        <button
          onClick={() => setShowGameLog(!showGameLog)}
          className="w-full flex items-center justify-between text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 sm:mb-3"
        >
          <span>Game Log ({session.games?.length || 0})</span>
          <span>{showGameLog ? '▲' : '▼'}</span>
        </button>
        
        {showGameLog && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg sm:rounded-xl overflow-hidden max-h-48 sm:max-h-60 overflow-y-auto">
            {(!session.games || session.games.length === 0) ? (
              <p className="text-center text-gray-600 py-4 text-sm">No games yet</p>
            ) : (
              session.games.map((game) => (
                <div
                  key={game.game_id}
                  className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-zinc-800 last:border-0"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-[10px] sm:text-xs text-gray-600 w-5 sm:w-6">#{game.game_number}</span>
                    <span className="font-bold text-sm sm:text-base">
                      <span className={game.winner === game.team_a ? 'text-green-500' : 'text-gray-400'}>{game.team_a}</span>
                      <span className="text-gray-600 mx-1">v</span>
                      <span className={game.winner === game.team_b ? 'text-green-500' : 'text-gray-400'}>{game.team_b}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2">
                    {game.three_win_rule_triggered && (
                      <Fire size={12} className="text-orange-500 sm:w-[14px] sm:h-[14px]" weight="fill" />
                    )}
                    <span className="text-[10px] sm:text-xs text-gray-500">
                      #{game.winner} W
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* BOTTOM CONTROLS (Fixed) */}
      {isAdmin && (
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-zinc-800 p-3 sm:p-4 safe-area-inset-bottom">
          <Button
            onClick={undoLastGame}
            disabled={processing || !session.games?.length}
            className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
          >
            <ArrowCounterClockwise size={20} className="mr-2 sm:w-6 sm:h-6" />
            UNDO LAST GAME
          </Button>
        </div>
      )}

      {/* EDIT QUEUE MODAL */}
      {showEditQueue && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h3 className="text-lg font-bold">Edit Waiting Order</h3>
              <button onClick={() => setShowEditQueue(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {editQueue.map((teamNum, index) => (
                <div
                  key={teamNum}
                  className="flex items-center justify-between bg-zinc-800 rounded-xl p-4"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-6">{index + 1}.</span>
                    <span className="text-xl font-bold">Team {teamNum}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveQueueTeam(index, -1)}
                      disabled={index === 0}
                      className="h-10 w-10"
                    >
                      <CaretUp size={20} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveQueueTeam(index, 1)}
                      disabled={index === editQueue.length - 1}
                      className="h-10 w-10"
                    >
                      <CaretDown size={20} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-800">
              <Button
                onClick={saveQueueOrder}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                Save Order
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM RESET MODAL */}
      {showConfirmReset && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md">
            <div className="p-6 text-center">
              <Warning size={64} className="mx-auto text-yellow-500 mb-4" weight="duotone" />
              <h3 className="text-xl font-bold mb-2">Reset This Run?</h3>
              <p className="text-gray-400 mb-6">
                This will delete all games and stats. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowConfirmReset(false)}
                  className="flex-1 h-12 bg-zinc-800 hover:bg-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={resetSession}
                  className="flex-1 h-12 bg-red-500 hover:bg-red-600 text-white"
                >
                  Reset Run
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* DETAILED REPORT MODAL */}
      {showReport && report && (
        <div className="fixed inset-0 bg-black/95 z-50 overflow-y-auto">
          <div className="min-h-screen p-4">
            <div className="max-w-2xl mx-auto">
              {/* Report Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black">SESSION REPORT</h2>
                  <p className="text-gray-400">{report.event_name}</p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setShowReport(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={24} />
                </Button>
              </div>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-orange-500">{report.summary.total_games}</p>
                  <p className="text-xs text-gray-500 uppercase">Total Games</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-yellow-500">{report.summary.longest_streak}</p>
                  <p className="text-xs text-gray-500 uppercase">Best Streak</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black text-red-500">{report.summary.three_win_triggers}</p>
                  <p className="text-xs text-gray-500 uppercase">3-Win Rules</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                  <p className="text-3xl font-black">{report.team_count}</p>
                  <p className="text-xs text-gray-500 uppercase">Teams</p>
                </div>
              </div>
              
              {/* Rankings */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="font-bold flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-500" />
                    Final Standings
                  </h3>
                </div>
                <div className="divide-y divide-zinc-800">
                  {report.rankings.map((team) => (
                    <div key={team.team_number} className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          team.rank === 1 ? 'bg-yellow-500 text-black' :
                          team.rank === 2 ? 'bg-gray-400 text-black' :
                          team.rank === 3 ? 'bg-orange-700 text-white' :
                          'bg-zinc-800 text-gray-400'
                        }`}>
                          {team.rank}
                        </span>
                        <div>
                          <p className="font-bold">Team {team.team_number}</p>
                          <p className="text-xs text-gray-500">
                            {team.total_wins}W - {team.total_losses}L ({team.win_percentage}%)
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-gray-400">Best: {team.longest_streak}</p>
                        <p className="text-orange-500">{team.three_win_runs} × 3-wins</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Head to Head */}
              {report.head_to_head.length > 0 && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
                  <div className="px-4 py-3 border-b border-zinc-800">
                    <h3 className="font-bold">Head-to-Head</h3>
                  </div>
                  <div className="divide-y divide-zinc-800 max-h-48 overflow-y-auto">
                    {report.head_to_head.map((h2h, i) => (
                      <div key={i} className="px-4 py-2 flex items-center justify-between text-sm">
                        <span className="text-gray-400">{h2h.matchup}</span>
                        <span className="font-mono font-bold">
                          <span className={h2h.team_a_wins > h2h.team_b_wins ? 'text-green-500' : 'text-gray-400'}>{h2h.team_a_wins}</span>
                          <span className="text-gray-600"> - </span>
                          <span className={h2h.team_b_wins > h2h.team_a_wins ? 'text-green-500' : 'text-gray-400'}>{h2h.team_b_wins}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Game Log */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-zinc-800">
                  <h3 className="font-bold">Complete Game Log</h3>
                </div>
                <div className="divide-y divide-zinc-800 max-h-64 overflow-y-auto">
                  {report.game_log.map((game) => (
                    <div key={game.game_number} className="px-4 py-2 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 w-6">#{game.game_number}</span>
                        <span>{game.matchup}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {game.three_win_rule && (
                          <Fire size={14} className="text-orange-500" weight="fill" />
                        )}
                        <span className="text-green-500 font-medium">{game.winner}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Close Button */}
              <Button
                onClick={() => setShowReport(false)}
                className="w-full h-12 bg-zinc-800 hover:bg-zinc-700 text-white font-bold"
              >
                Close Report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenRunTracker;
